/**
 * §P.1 · Los pagadores del club.
 *
 * La columna que justifica esta pantalla es **«ve sus recibos»**: `user_id` es lo
 * único que une un recibo con la app de familias, y sin él el recibo existe y la
 * madre no lo ve. Un pagador suelto es legítimo —una empresa, un abuelo sin
 * móvil— pero tiene que **verse**, porque si no se descubre cuando alguien llama
 * preguntando por un recibo que no le ha llegado.
 */
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import {
  anterior,
  cursorActual,
  esLaPrimera,
  numeroDePagina,
  siguiente,
  PRIMERA,
  type PilaDeCursores,
} from '@/shared/ui/paginas';
import { sinCuenta, type PaginaDePagadores } from '@/club/pagadores/tipos';
import {
  FormularioDePagador,
  type DesdeTutor,
} from '@/club/pagadores/FormularioDePagador';

export function ListaDePagadores({
  club,
  puedeEscribir,
  suspendido,
  desdeTutor,
  alTerminarConTutor,
  alAbrir,
}: {
  club: string;
  puedeEscribir: boolean;
  suspendido: boolean;
  /** Si se llega desde un tutor de la ficha de un alumno, sus datos. */
  desdeTutor?: DesdeTutor | undefined;
  alTerminarConTutor: () => void;
  alAbrir: (pagadorId: string) => void;
}) {
  const [creando, setCreando] = useState(false);
  const [q, setQ] = useState('');
  const [cursores, setCursores] = useState<PilaDeCursores>(PRIMERA);
  const cursor = cursorActual(cursores);

  const pagadores = useQuery({
    queryKey: ['pagadores', club, q, cursor],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).payers.get({
        query: {
          limit: 25,
          ...(cursor ? { cursor } : {}),
          ...(q ? { q } : {}),
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDePagadores;
    },
  });

  // Llegar con un tutor es llegar directo al alta: quien pulsó «hacer pagador»
  // en una ficha no quiere ver primero una lista.
  if (creando || desdeTutor) {
    return (
      <FormularioDePagador
        club={club}
        pagador={null}
        etag={null}
        desdeTutor={desdeTutor}
        suspendido={suspendido}
        alGuardar={(id) => {
          setCreando(false);
          alTerminarConTutor();
          alAbrir(id);
        }}
        alCancelar={() => {
          setCreando(false);
          alTerminarConTutor();
        }}
      />
    );
  }

  const sueltos = (pagadores.data?.data ?? []).filter(sinCuenta).length;

  return (
    <section>
      <header className="titulo-fila">
        <h1>Pagadores</h1>
        {puedeEscribir && (
          <button type="button" onClick={() => setCreando(true)}>
            Nuevo pagador
          </button>
        )}
      </header>

      <p className="tenue">
        Quien recibe el recibo. No es el alumno y no siempre es el tutor: una
        madre con tres hijos recibe <strong>un</strong> recibo con las líneas de
        los tres.
      </p>

      <div className="filtros">
        <label>
          <span className="sr">Buscar</span>
          <input
            type="search"
            placeholder="Nombre, NIF o correo"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCursores(PRIMERA);
            }}
          />
        </label>
      </div>

      {sueltos > 0 && (
        // El dato que nadie pide y todo el mundo necesita.
        <div className="nota">
          <strong>
            {sueltos} de esta página {sueltos === 1 ? 'no tiene' : 'no tienen'}{' '}
            cuenta de la app.
          </strong>{' '}
          Sus recibos se emiten igual, pero no se ven en el móvil de nadie.
        </div>
      )}

      {pagadores.isPending && (
        <Cargando filas={5} etiqueta="Cargando pagadores" />
      )}

      {pagadores.isError && (
        <PanelError
          problema={aProblema(pagadores.error)}
          reintentar={() => void pagadores.refetch()}
        />
      )}

      {pagadores.data?.data.length === 0 &&
        (q ? (
          <Vacio
            titulo="Ningún pagador encaja con eso"
            explicacion="Prueba con parte del nombre, el NIF o el correo."
            accion={
              <button type="button" onClick={() => setQ('')}>
                Quitar la búsqueda
              </button>
            }
          />
        ) : (
          <Vacio
            titulo="Todavía no hay pagadores"
            explicacion="Un pagador es quien recibe el recibo. Lo normal es crearlo desde la ficha de un alumno, sobre su tutor: así queda vinculado a su cuenta y ve los recibos en la app."
            {...(puedeEscribir
              ? {
                  accion: (
                    <button type="button" onClick={() => setCreando(true)}>
                      Crear uno sin vincular
                    </button>
                  ),
                }
              : {})}
          />
        ))}

      {pagadores.data && pagadores.data.data.length > 0 && (
        <>
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Pagador</th>
                  <th>NIF</th>
                  <th>Contacto</th>
                  <th>Ve sus recibos</th>
                </tr>
              </thead>
              <tbody>
                {pagadores.data.data.map((p) => (
                  <tr
                    key={p.id}
                    className="fila-pulsable"
                    onClick={() => alAbrir(p.id)}
                  >
                    <td>
                      <button type="button" className="enlace-fila">
                        {p.legal_name}
                      </button>
                    </td>
                    <td className="tenue">{p.tax_id ?? '—'}</td>
                    <td className="tenue">{p.email ?? p.phone ?? '—'}</td>
                    <td>
                      {sinCuenta(p) ? (
                        <span
                          className="pastilla pastilla-paused"
                          title="Sin cuenta de la app: los recibos se emiten pero no se ven en el móvil"
                        >
                          No, sin cuenta
                        </span>
                      ) : (
                        <span className="pastilla pastilla-active">Sí</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <nav className="paginacion">
            <button
              type="button"
              className="btn-plano"
              disabled={esLaPrimera(cursores) || pagadores.isFetching}
              onClick={() => setCursores(anterior)}
            >
              Anterior
            </button>
            <span className="tenue">
              {esLaPrimera(cursores)
                ? 'Primeros 25'
                : `Página ${numeroDePagina(cursores)}`}
            </span>
            <button
              type="button"
              className="btn-plano"
              disabled={!pagadores.data.has_more || pagadores.isFetching}
              onClick={() =>
                setCursores((c) =>
                  siguiente(c, pagadores.data?.next_cursor ?? null)
                )
              }
            >
              Siguiente
            </button>
          </nav>
        </>
      )}
    </section>
  );
}
